import { useState, useEffect, useRef, useCallback } from 'react';
import CONFIG, { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, CREDIT_PREDICTION, CREDIT_FALLBACK } from '../config';
import { db } from '../firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { fetchWithAuth } from '../utils/api';
import { downloadFile } from '../utils/downloadUtils';

export const useTranslateAnalysis = (selectedPpt, userId, credit, predictionData, showAlertModal, showConfirmModal, t) => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] = useState({ status: '', completed: false });
  const [translateStatus, setTranslateStatus] = useState(''); // 'translating', 'completed', 'error'

  // 선택된 PPT의 번역된 언어 목록을 Firestore에서 확인
  // ✅ 구조: { lang: { download_url, filename, completed_at, status, error } }
  const [translatedLanguages, setTranslatedLanguages] = useState({});

  // 리스너 참조 저장 (탭 전환 시 복구용)
  const unsubscribeRef = useRef(null);
  const currentTranslatingLangRef = useRef(null);

  // 번역 진행상황 실시간 리스너 (download_url, error 포함)
  const subscribeToTranslationProgress = useCallback((docId, targetLanguage, callback) => {
    const progressRef = doc(db, 'PPT_FILE', docId, 'translation_results', 'analysis_progress');

    return onSnapshot(progressRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        callback({
          status: data.status || '',
          completed: data.completed || false,
          download_url: data.download_url || null,
          filename: data.filename || null,
          error: data.error || null,
          current_language: data.current_language || null,
          total_cost_krw: data.total_cost_krw || 0
        });
      }
    }, (error) => {
      console.error('번역 진행률 구독 오류:', error);
      callback({ error: error.message });
    });
  }, []);

  // 진행상황 업데이트 핸들러
  const handleProgressUpdate = useCallback((data, targetLanguage) => {
    if (data.error && !data.download_url) {
      // 에러 발생
      console.log(`[useTranslateAnalysis] 번역 에러: ${data.error}`);

      setTranslatedLanguages(prev => ({
        ...prev,
        [targetLanguage]: {
          ...prev[targetLanguage],
          status: 'error',
          error: data.error
        }
      }));

      setIsTranslating(false);
      setTranslateStatus('error');
      setTranslateProgress({ status: t('hooks.translate.failed'), completed: false });

      // 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      currentTranslatingLangRef.current = null;

      showAlertModal(t('hooks.translate.errorDuringTranslate', { error: data.error }), 'error');
      return;
    }

    // 진행 상황 업데이트
    setTranslateProgress({
      status: data.status || '',
      completed: data.completed || false
    });

    // 완료 + download_url 수신
    if (data.completed && data.download_url) {
      console.log(`[useTranslateAnalysis] 번역 완료: ${targetLanguage}, URL: ${data.download_url}`);

      setTranslatedLanguages(prev => ({
        ...prev,
        [targetLanguage]: {
          download_url: data.download_url,
          filename: data.filename || null,
          completed_at: new Date(),
          status: 'completed',
          error: null
        }
      }));

      setIsTranslating(false);
      setTranslateStatus('completed');

      // 리스너 해제
      if (unsubscribeRef.current) {
        console.log('[useTranslateAnalysis] 번역 완료, 리스너 해제');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      currentTranslatingLangRef.current = null;

      // 완료 모달 표시
      const usedCredit = data.total_cost_krw || 0;
      showConfirmModal(
        t('hooks.translate.complete', {
          language: LANGUAGE_NAMES[targetLanguage] || targetLanguage,
          usedCredit: Math.ceil(usedCredit)
        })
      ).then(async (confirmed) => {
        if (confirmed) {
          // Blob 방식으로 다운로드 (Edge 호환)
          await downloadFile(data.download_url, data.filename || 'translated.pptx');
        }
      });
    }
  }, [showAlertModal, showConfirmModal, t]);

  // PPT 선택 시 번역된 언어 확인 + 진행 중인 번역 복구
  useEffect(() => {
    const checkTranslatedLanguages = async () => {
      if (!selectedPpt) {
        setTranslatedLanguages({});
        setTranslateStatus('');
        return;
      }

      try {
        const supportedLanguageCodes = SUPPORTED_LANGUAGES.map(lang => lang.code);
        const translatedLangsData = {};

        // 1. 완료된 번역 확인
        for (const lang of supportedLanguageCodes) {
          const langDocRef = doc(db, 'PPT_FILE', selectedPpt, 'translation_results', lang);
          const langDocSnap = await getDoc(langDocRef);

          if (langDocSnap.exists()) {
            const data = langDocSnap.data();
            translatedLangsData[lang] = {
              download_url: data.download_url || null,
              filename: data.filename || null,
              completed_at: data.completed_at || null,
              status: 'completed',
              error: null
            };
          }
        }

        // 2. 진행 중인 번역 확인 (analysis_progress)
        const progressRef = doc(db, 'PPT_FILE', selectedPpt, 'translation_results', 'analysis_progress');
        const progressSnap = await getDoc(progressRef);

        if (progressSnap.exists()) {
          const progressData = progressSnap.data();
          const currentLang = progressData.current_language;

          // 진행 중인 번역이 있고, 아직 완료되지 않은 경우
          if (currentLang && !progressData.completed && !progressData.error) {
            console.log(`[useTranslateAnalysis] 진행 중인 번역 발견: ${currentLang}`);

            // translatedLanguages에 진행 중 상태 추가
            translatedLangsData[currentLang] = {
              download_url: null,
              filename: null,
              completed_at: null,
              status: 'translating',
              error: null
            };

            // 리스너 시작
            setIsTranslating(true);
            setTranslateStatus('translating');
            currentTranslatingLangRef.current = currentLang;

            // 기존 리스너 해제
            if (unsubscribeRef.current) {
              unsubscribeRef.current();
            }

            unsubscribeRef.current = subscribeToTranslationProgress(selectedPpt, currentLang, (data) => {
              handleProgressUpdate(data, currentLang);
            });
          }

          // 에러 상태인 경우
          if (currentLang && progressData.error) {
            translatedLangsData[currentLang] = {
              download_url: null,
              filename: null,
              completed_at: null,
              status: 'error',
              error: progressData.error
            };
          }
        }

        setTranslatedLanguages(translatedLangsData);

        if (Object.keys(translatedLangsData).length > 0) {
          setTranslateStatus('completed');
        } else {
          setTranslateStatus('');
        }
      } catch (error) {
        console.error('번역 상태 확인 오류:', error);
        setTranslatedLanguages({});
        setTranslateStatus('');
      }
    };

    checkTranslatedLanguages();

    // 컴포넌트 언마운트 시 리스너 해제
    return () => {
      if (unsubscribeRef.current) {
        console.log('[useTranslateAnalysis] 리스너 해제 (언마운트)');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [selectedPpt, subscribeToTranslationProgress, handleProgressUpdate]);

  // PPT 번역 (비동기 방식)
  const translatePPT = async (targetLanguage) => {
    // Credit 체크 - 예측 기반 또는 fallback
    const singlePromptTokens = predictionData?.translation?.single_prompt_tokens;
    const requiredCredit = singlePromptTokens
      ? Math.ceil(singlePromptTokens * CREDIT_PREDICTION.TRANSLATION_RATE) * CREDIT_PREDICTION.MULTIPLIER
      : CREDIT_FALLBACK.TRANSLATION;

    if (credit < requiredCredit) {
      showAlertModal(t('hooks.translate.insufficientCredit'), 'warning');
      return;
    }

    if (!selectedPpt || !userId) {
      showAlertModal(t('hooks.translate.noDocOrUser'), 'error');
      return;
    }

    setIsTranslating(true);
    setTranslateStatus('translating');
    setTranslateProgress({ status: t('hooks.translate.preparing'), completed: false });
    currentTranslatingLangRef.current = targetLanguage;

    // 즉시 카드 생성 (status: 'translating')
    setTranslatedLanguages(prev => ({
      ...prev,
      [targetLanguage]: {
        download_url: null,
        filename: null,
        completed_at: null,
        status: 'translating',
        error: null
      }
    }));

    try {
      // 번역 API 호출 (즉시 응답)
      const translateResponse = await fetchWithAuth(`${CONFIG.CLOUD_FUNCTION_URL}/translate_ppt`, {
        method: 'POST',
        body: JSON.stringify({
          doc_id: selectedPpt,
          target_language: targetLanguage,
          userId: userId
        })
      });

      const translateData = await translateResponse.json();
      console.log('[useTranslateAnalysis] API 응답:', translateData);

      if (!translateResponse.ok) {
        throw new Error(translateData.error || t('hooks.translate.generalError'));
      }

      // 즉시 응답 수신 - 백그라운드 처리 시작됨
      if (translateData.success) {
        console.log(`[useTranslateAnalysis] 번역 시작됨: ${targetLanguage}`);

        // 기존 리스너 해제
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
        }

        // 리스너 시작 (download_url 수신까지 유지)
        unsubscribeRef.current = subscribeToTranslationProgress(selectedPpt, targetLanguage, (data) => {
          handleProgressUpdate(data, targetLanguage);
        });
      }

    } catch (error) {
      console.error('번역 시작 오류:', error);

      // 에러 상태로 카드 업데이트
      setTranslatedLanguages(prev => ({
        ...prev,
        [targetLanguage]: {
          download_url: null,
          filename: null,
          completed_at: null,
          status: 'error',
          error: error.message
        }
      }));

      setIsTranslating(false);
      setTranslateStatus('error');
      currentTranslatingLangRef.current = null;

      showAlertModal(error.message || t('hooks.translate.errorGeneric'), 'error');
    }
  };

  // 번역된 PPT 다운로드
  const downloadTranslatedPPT = async (targetLanguage) => {
    if (!targetLanguage) {
      showAlertModal(t('hooks.translate.selectLanguage'), 'warning');
      return;
    }

    const langData = translatedLanguages[targetLanguage];

    if (!langData || !langData.download_url) {
      showAlertModal(t('hooks.translate.noDownloadUrl'), 'error');
      return;
    }

    try {
      // Blob 방식으로 다운로드 (Edge 호환)
      const success = await downloadFile(langData.download_url, langData.filename || 'translated.pptx');

      if (success) {
        showAlertModal(t('hooks.translate.startDownload', { language: LANGUAGE_NAMES[targetLanguage] || targetLanguage }), 'success');
      } else {
        showAlertModal(t('hooks.translate.downloadError'), 'error');
      }
    } catch (error) {
      console.error('다운로드 오류:', error);
      showAlertModal(t('hooks.translate.downloadError'), 'error');
    }
  };

  // 번역 실행 (중복 체크 포함)
  const handleTranslatePPT = async (targetLanguage) => {
    const langData = translatedLanguages[targetLanguage];

    // 진행 중인 경우 무시
    if (langData && langData.status === 'translating') {
      showAlertModal(t('hooks.translate.inProgress'), 'warning');
      return;
    }

    // 이미 완료된 번역이 있는 경우 (에러 제외)
    if (langData && langData.status === 'completed') {
      const confirmed = await showConfirmModal(
        t('hooks.translate.alreadyTranslated', { language: LANGUAGE_NAMES[targetLanguage] || targetLanguage })
      );

      if (confirmed) {
        translatePPT(targetLanguage);
      }
    } else {
      // 새 번역 또는 에러 재시도
      translatePPT(targetLanguage);
    }
  };

  return {
    isTranslating,
    translateProgress,
    translateStatus,
    translatedLanguages,
    translatePPT: handleTranslatePPT,
    downloadTranslatedPPT
  };
};
